/**
 * Image URL utilities for handling various image sources
 */

/**
 * Convert Google Drive sharing links to direct image URLs
 * 
 * Supported formats:
 * - https://drive.google.com/file/d/{id}/view
 * - https://drive.google.com/file/d/{id}/view?usp=sharing
 * - https://drive.google.com/open?id={id}
 * - https://drive.google.com/uc?id={id}
 * 
 * @param {string} url - The input URL
 * @returns {string} - A direct image URL (converted if Google Drive, otherwise unchanged)
 */
export function convertGoogleDriveUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }

  const trimmedUrl = url.trim();

  // Pattern 1: drive.google.com/file/d/{id}/view
  const filePattern = /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/;
  const fileMatch = trimmedUrl.match(filePattern);
  if (fileMatch && fileMatch[1]) {
    return `https://drive.google.com/uc?export=view&id=${fileMatch[1]}`;
  }

  // Pattern 2: drive.google.com/open?id={id}
  const openPattern = /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/;
  const openMatch = trimmedUrl.match(openPattern);
  if (openMatch && openMatch[1]) {
    return `https://drive.google.com/uc?export=view&id=${openMatch[1]}`;
  }

  // Pattern 3: drive.google.com/uc?id={id} (already a direct link, but ensure export param)
  const ucPattern = /drive\.google\.com\/uc\?(?:.*&)?id=([a-zA-Z0-9_-]+)/;
  const ucMatch = trimmedUrl.match(ucPattern);
  if (ucMatch && ucMatch[1]) {
    return `https://drive.google.com/uc?export=view&id=${ucMatch[1]}`;
  }

  // Not a Google Drive URL, return as-is
  return trimmedUrl;
}

/**
 * Check if a URL is a Google Drive URL
 * @param {string} url - The URL to check
 * @returns {boolean}
 */
export function isGoogleDriveUrl(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }
  return url.includes('drive.google.com');
}

/**
 * Get fallback URL for Google Drive (using lh3.googleusercontent.com)
 * @param {string} url - The original Google Drive URL
 * @returns {string|null} - Fallback URL or null if not a Google Drive URL
 */
export function getGoogleDriveFallbackUrl(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  // Extract file ID from any Google Drive URL format
  const patterns = [
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
    /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/,
    /drive\.google\.com\/uc\?(?:.*&)?id=([a-zA-Z0-9_-]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://lh3.googleusercontent.com/d/${match[1]}`;
    }
  }

  return null;
}

export default {
  convertGoogleDriveUrl,
  isGoogleDriveUrl,
  getGoogleDriveFallbackUrl
};
